import React from 'react'
import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonButton } from '@ionic/react'
import { checkAndClearOldData } from '../utils/versionManager'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

class ErrorBoundary extends React.Component<
  React.PropsWithChildren,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  handleClearCacheAndReload = async () => {
    try {
      await checkAndClearOldData()
      window.location.reload()
    } catch (error) {
      console.error('Error clearing cache:', error)
      window.location.reload()
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <IonPage>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Something went wrong</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="text-center py-8">
              <h2 className="text-xl font-bold mb-4">Oops! Something went wrong</h2>
              <p className="text-gray-600 mb-4">
                The application encountered an error. Please try refreshing the page.
              </p>
              <div className="space-y-3 max-w-sm mx-auto">
                <IonButton 
                  expand="block" 
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </IonButton>
                <IonButton 
                  expand="block" 
                  fill="outline"
                  onClick={this.handleClearCacheAndReload}
                >
                  Clear Cache & Reload
                </IonButton>
              </div>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mt-6 p-4 bg-red-50 rounded-lg text-left">
                  <h3 className="font-bold text-red-800 mb-2">Error Details:</h3>
                  <pre className="text-sm text-red-700 overflow-auto">
                    {this.state.error.toString()}
                  </pre>
                </div>
              )}
            </div>
          </IonContent>
        </IonPage>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary 